// src/components/StepIndicator.jsx
import React from 'react';

export default function StepIndicator({
  steps = [1, 2, 3, 4, 5, 6],
  current = 1,
  onStepClick = () => {},
  size = 'sm',
  colorMap = { infirmiere: [1, 2, 5, 6], medecin: [3, 4] },
  variant = 'auto', // "auto" | "pills" | "path"
  desktopFull = false, // étendre en largeur sur desktop
  className = '',
}) {
  const med = new Set(colorMap?.medecin || []);
  const inf = new Set(colorMap?.infirmiere || []);
  const toneOf = (s) => (med.has(s) ? 'red' : inf.has(s) ? 'emerald' : 'slate');

  const SIZES = {
    xs: { bubble: 'h-8 w-8 text-[13px] rounded-xl', pad: 'px-2' },
    sm: { bubble: 'h-10 w-10 text-[15px] rounded-2xl', pad: 'px-3' },
    md: { bubble: 'h-12 w-12 text-[16px] rounded-2xl', pad: 'px-4' },
  };
  const sz = SIZES[size] || SIZES.sm;

  const bubbleClass = (s, active) => {
    const t = toneOf(s);
    const toneBg = t === 'red' ? 'bg-red-600' : t === 'emerald' ? 'bg-emerald-600' : 'bg-slate-600';
    const toneBorder =
      t === 'red' ? 'border-red-400' : t === 'emerald' ? 'border-emerald-400' : 'border-slate-300';
    const toneText = active
      ? 'text-white'
      : t === 'red'
        ? 'text-red-600'
        : t === 'emerald'
          ? 'text-emerald-600'
          : 'text-slate-600';
    const toneHover = active ? '' : t === 'red' ? 'hover:bg-red-50' : 'hover:bg-emerald-50';

    return [
      'grid place-items-center select-none transition shadow-sm',
      active ? `${toneBg} ${toneText}` : `bg-white border ${toneBorder} ${toneText} ${toneHover}`,
      sz.bubble,
    ].join(' ');
  };

  const connectorClass = (fromStep) => {
    const completed = Number(fromStep) < Number(current);
    const t = toneOf(fromStep + 1);
    const color = completed
      ? t === 'red'
        ? 'bg-red-500'
        : t === 'emerald'
          ? 'bg-emerald-500'
          : 'bg-slate-400'
      : 'bg-slate-200';
    return `flex-1 h-[3px] rounded-full ${color}`;
  };

  /* --- Rendu “pills” --- */
  const Pills = (
    <div className={['flex flex-wrap items-center justify-center gap-2 px-2', className].join(' ')}>
      {steps.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onStepClick(s)}
          className={bubbleClass(s, Number(current) === Number(s))}
          aria-current={Number(current) === Number(s) ? 'step' : undefined}
          aria-label={`Étape ${s}`}
        >
          {s}
        </button>
      ))}
    </div>
  );

  /* --- Rendu “chemin” (avec traits) --- */
  const Path = (
    <div
      className={[
        desktopFull ? 'flex w-full items-center' : 'flex items-center',
        sz.pad,
        className,
      ].join(' ')}
    >
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <button
            type="button"
            onClick={() => onStepClick(s)}
            className={bubbleClass(s, Number(current) === Number(s))}
            aria-current={Number(current) === Number(s) ? 'step' : undefined}
            aria-label={`Étape ${s}`}
          >
            {s}
          </button>
          {i < steps.length - 1 && <div className={connectorClass(s)} />}
        </React.Fragment>
      ))}
    </div>
  );

  if (variant === 'pills') return Pills;
  if (variant === 'path') return Path;

  // auto: pills en mobile, path en desktop
  return (
    <div className="w-full">
      <div className="md:hidden">{Pills}</div>
      <div className="hidden md:block">{Path}</div>
    </div>
  );
}
